import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, Clock, User, ArrowLeft } from 'lucide-react';
import { FaSpotify, FaApple, FaYoutube } from 'react-icons/fa';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { siteConfig } from '../mockData';
import { API_URL } from '../utils/apiUrl';
import { formatSeries } from '../utils/formatSeries';
import { extractYouTubeId } from '../utils/extractYouTubeId';
import { buildEpisodeJsonLd } from '../utils/buildEpisodeJsonLd';
import { SEOHead } from '../components/SEOHead';
import { JoinClubhouseCTA } from '../components/JoinClubhouseCTA';
import './EpisodeDetail.css';

const PUBLIC_SITE_URL = process.env.REACT_APP_PUBLIC_SITE_URL || '';

export const EpisodeDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [episode, setEpisode] = useState(null);
  const [episodeGuests, setEpisodeGuests] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEpisodeData = async () => {
      try {
        setLoading(true);
        
        // Fetch all episodes, guests, and site settings from CMS
        const [episodesRes, guestsRes, settingsRes] = await Promise.all([
          axios.get(`${API_URL}/api/cms/episodes`),
          axios.get(`${API_URL}/api/cms/guests`),
          axios.get(`${API_URL}/api/cms/settings`).catch(() => ({ data: null }))
        ]);

        // Store site settings for YouTube fallback
        setSiteSettings(settingsRes.data);

        // Find the episode by current slug, with fallback to historical slugs
        // (so newsletter / social links pointing at old slugs still resolve)
        let foundEpisode = episodesRes.data.find(ep => ep.slug === slug);
        if (!foundEpisode) {
          foundEpisode = episodesRes.data.find(ep =>
            Array.isArray(ep.slugHistory) && ep.slugHistory.includes(slug)
          );
        }
        
        if (!foundEpisode) {
          setError('Episode not found');
          setLoading(false);
          return;
        }

        // Canonicalize URL: if user arrived via an old slug, redirect to the current one
        if (foundEpisode.slug && foundEpisode.slug !== slug) {
          navigate(`/episodes/${foundEpisode.slug}`, { replace: true });
          return;
        }

        setEpisode(foundEpisode);

        // Find guests for this episode
        if (foundEpisode.guests && foundEpisode.guests.length > 0) {
          const matchedGuests = guestsRes.data.filter(g => 
            foundEpisode.guests.includes(g.id)
          );
          setEpisodeGuests(matchedGuests);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching episode:', err);
        setError('Failed to load episode');
        setLoading(false);
      }
    };

    fetchEpisodeData();
  }, [slug, navigate]);

  /**
   * Compute the YouTube target URL for the "Watch on YouTube" button
   * Fallback logic:
   * 1. If episode has youtubeUrl (valid YouTube URL) → use it
   * 2. Else if episode has youtubeId (video ID) → build watch URL
   * 3. Else → use global YouTube channel URL from Site Settings
   * 4. Final fallback → https://www.youtube.com
   */
  const getYouTubeTargetUrl = () => {
    // Helper to validate YouTube URL
    const isValidYouTubeUrl = (url) => {
      if (!url || typeof url !== 'string') return false;
      const trimmed = url.trim();
      if (!trimmed) return false;
      try {
        const parsed = new URL(trimmed);
        return ['youtube.com', 'www.youtube.com', 'youtu.be', 'm.youtube.com'].includes(parsed.hostname);
      } catch {
        return false;
      }
    };

    // 1. Check for episode.youtubeUrl (full URL)
    if (episode?.youtubeUrl && isValidYouTubeUrl(episode.youtubeUrl)) {
      return episode.youtubeUrl;
    }

    // 2. Check for episode.youtubeId (video ID or URL with ID)
    const videoId = extractYouTubeId(episode?.youtubeId || episode?.youtubeUrl);
    if (videoId) {
      return `https://www.youtube.com/watch?v=${videoId}`;
    }

    // 3. Fallback to global YouTube channel URL from Site Settings
    const channelUrl = siteSettings?.social?.youtube?.channelUrl || 
                       siteSettings?.podcast?.youtube;
    
    if (channelUrl && isValidYouTubeUrl(channelUrl)) {
      return channelUrl;
    }

    // 4. Final fallback - log warning and use generic YouTube
    console.warn('[EpisodeDetail] Missing YouTube configuration: No episode YouTube URL/ID and no global channel URL in Site Settings');
    return 'https://www.youtube.com';
  };

  if (error || !episode) {
    return (
      <div className="episode-not-found">
        <div className="container">
          <h1 className="heading-1">Episode Not Found</h1>
          <p>The episode you&apos;re looking for doesn&apos;t exist or has been removed.</p>
          <Link to="/episodes" className="btn-primary">Back to Episodes</Link>
        </div>
      </div>
    );
  }

  // Strip HTML tags for description preview in meta tags
  const plainDescription = (() => {
    if (!episode.description) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = episode.description;
    return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim().slice(0, 200);
  })();

  const canonicalUrl = PUBLIC_SITE_URL ? `${PUBLIC_SITE_URL}/episodes/${episode.slug}` : null;

  // Build structured data for rich results (PodcastEpisode + VideoObject)
  const jsonLd = buildEpisodeJsonLd({
    episode,
    canonicalUrl: canonicalUrl || `${window.location.origin}/episodes/${episode.slug}`,
    siteUrl: PUBLIC_SITE_URL || window.location.origin,
    youtubeId: extractYouTubeId(episode.youtubeId),
    description: plainDescription,
  });

  return (
    <div className="episode-detail-page">
      <SEOHead
        title={episode.title}
        description={plainDescription}
        path={`/episodes/${episode.slug}`}
        ogType="article"
        image={
          episode.thumbnail ||
          (extractYouTubeId(episode.youtubeId)
            ? `https://img.youtube.com/vi/${extractYouTubeId(episode.youtubeId)}/hqdefault.jpg`
            : null)
        }
      />
      {jsonLd && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}

      <div className="container">
        <Link to="/episodes" className="back-link">
          <ArrowLeft size={20} />
          <span>Back to Episodes</span>
        </Link>

        <div className="episode-header">
          <span className="episode-series-badge-detail">{formatSeries(episode.series)}</span>
          <h1 className="display-medium">{episode.title}</h1>
          
          <div className="episode-meta-detail">
            <span className="meta-item">
              <Calendar size={18} />
              {new Date(episode.publishDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
            <span className="meta-item">
              <Clock size={18} />
              {episode.duration}
            </span>
            {episodeGuests.length > 0 && (
              <span className="meta-item">
                <User size={18} />
                {episodeGuests.map(g => g.name).join(', ')}
              </span>
            )}
          </div>
        </div>

        {/* Video Player or Thumbnail */}
        <div className="episode-player-section">
          <div className="episode-player-large">
            {(() => {
              const youtubeId = extractYouTubeId(episode.youtubeId);
              const hasValidYouTube = youtubeId && /^[a-zA-Z0-9_-]{11}$/.test(youtubeId);
              
              // Check if showComingSoon flag is explicitly set
              const showComingSoon = episode.showComingSoon === true;
              
              // Show thumbnail if no valid YouTube ID or showComingSoon is true
              if (!hasValidYouTube || showComingSoon) {
                return (
                  <div className="episode-thumbnail-large">
                    <img 
                      src={episode.thumbnail || 'https://via.placeholder.com/1280x720?text=Coming+Soon'} 
                      alt={episode.title}
                      className="episode-thumbnail-img"
                    />
                    <div className="coming-soon-overlay">
                      <h3>Coming Soon</h3>
                      <p>This episode will be available on {new Date(episode.publishDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                    </div>
                  </div>
                );
              }
              
              // Show real YouTube video if available
              return (
                <iframe
                  src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`}
                  title={episode.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="youtube-embed-large"
                ></iframe>
              );
            })()}
          </div>
        </div>

        {/* Listen On Section */}
        <div className="listen-on-section">
          <h3 className="heading-3">Listen on your favorite platform:</h3>
          <div className="platform-buttons">
            {(() => {
              // Spotify button with episode-specific URL fallback to global
              const spotifyUrl = episode?.spotifyUrl || siteSettings?.podcast?.spotify || siteConfig.podcast.spotify;
              const hasSpotify = spotifyUrl && spotifyUrl.trim() !== '';
              
              if (hasSpotify) {
                return (
                  <a 
                    href={spotifyUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="platform-btn platform-btn-spotify"
                  >
                    <FaSpotify size={20} />
                    <span>Listen on Spotify</span>
                  </a>
                );
              } else {
                return (
                  <button 
                    className="platform-btn platform-btn-spotify platform-btn-disabled"
                    disabled
                    title="Spotify link not available"
                  >
                    <FaSpotify size={20} />
                    <span>Link not available</span>
                  </button>
                );
              }
            })()}
            
            {(() => {
              // Apple Podcasts button with episode-specific URL fallback to global
              const appleUrl = episode?.applePodcastsUrl || siteSettings?.podcast?.apple || siteConfig.podcast.apple;
              const hasApple = appleUrl && appleUrl.trim() !== '';
              
              if (hasApple) {
                return (
                  <a 
                    href={appleUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="platform-btn platform-btn-apple"
                  >
                    <FaApple size={20} />
                    <span>Listen on Apple Podcasts</span>
                  </a>
                );
              } else {
                return (
                  <button 
                    className="platform-btn platform-btn-apple platform-btn-disabled"
                    disabled
                    title="Apple Podcasts link not available"
                  >
                    <FaApple size={20} />
                    <span>Link not available</span>
                  </button>
                );
              }
            })()}
            
            <a 
              href={getYouTubeTargetUrl()} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="platform-btn platform-btn-youtube"
            >
              <FaYoutube size={20} />
              <span>Watch on YouTube</span>
            </a>
          </div>
        </div>

        {/* Episode Description */}
        <div className="episode-content">
          <div className="episode-description-section">
            <h2 className="heading-2">About This Episode</h2>
            {(() => {
              // Check if description contains HTML tags (rich text)
              const isRichText = episode.description && /<[a-z][\s\S]*>/i.test(episode.description);
              
              if (isRichText) {
                // Sanitize and render rich text content
                // Allow only safe tags: p, br, strong, b, em, i, u, ul, ol, li, a, h3, h4
                const sanitizedContent = DOMPurify.sanitize(episode.description, {
                  ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'a', 'h3', 'h4', 'span', 'div'],
                  ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
                  ADD_ATTR: ['target'], // Allow target attribute for links
                });
                
                // Add target="_blank" and rel="noopener noreferrer" to all links
                const contentWithSecureLinks = sanitizedContent.replace(
                  /<a\s+href="([^"]+)"([^>]*)>/gi,
                  '<a href="$1" target="_blank" rel="noopener noreferrer"$2>'
                );
                
                return (
                  <div 
                    className="episode-description-content"
                    dangerouslySetInnerHTML={{ __html: contentWithSecureLinks }}
                  />
                );
              } else {
                // Render plain text (legacy content)
                return <p className="body-large">{episode.description || 'No description available.'}</p>;
              }
            })()}
          </div>

          {/* Timestamps */}
          {episode.highlights && episode.highlights.length > 0 && (
            <div className="timestamps-section">
              <h2 className="heading-2">Timestamps</h2>
              <ul className="timestamps-list">
                {episode.highlights.map((highlight, index) => (
                  <li key={`timestamp-${highlight.time || ''}-${index}`} className="timestamp-item">
                    <span className="timestamp-time">{highlight.time}</span>
                    <span className="timestamp-description">{highlight.description}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Guest Cards */}
        {episodeGuests.length > 0 && (
          <div className="episode-guests-section">
            <h2 className="heading-2">Featured Guests</h2>
            <div className="episode-guests-grid">
              {episodeGuests.map(guest => (
                <Link to={`/guests/${guest.id}`} key={guest.id} className="episode-guest-card hover-lift">
                  <img src={guest.image} alt={guest.name} className="guest-card-image" />
                  <div className="guest-card-info">
                    <h3 className="heading-3">{guest.name}</h3>
                    <p className="guest-card-role">{guest.role}</p>
                    <p className="guest-card-company">{guest.company}</p>
                    <p className="body-small">{guest.bio}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Membership CTA — converts SEO traffic on episode pages */}
        <JoinClubhouseCTA variant="episode" />
      </div>
    </div>
  );
};
